type Slide = {
  image: string;
  quote: string;
  author: string;
  role: string;
};

const SLIDES: Slide[] = [
  {
    image:
      "https://images.unsplash.com/photo-1669127300649-940337f1487e?auto=format&fit=crop&w=1400&q=80",
    quote:
      "Engineering is the art of directing the great sources of power in nature for the use and convenience of man.",
    author: "Thomas Tredgold",
    role: "Civil Engineer, 1788–1829",
  },
  {
    image:
      "https://images.unsplash.com/photo-1594750852563-5ed8e0421d40?auto=format&fit=crop&w=1400&q=80",
    quote: "Education is the most powerful weapon which you can use to change the world.",
    author: "Nelson Mandela",
    role: "Nobel Peace Prize Laureate",
  },
  {
    image:
      "https://images.unsplash.com/photo-1611348524140-53c9a25263d6?auto=format&fit=crop&w=1400&q=80",
    quote: "An investment in knowledge pays the best interest.",
    author: "Benjamin Franklin",
    role: "Polymath & Statesman",
  },
  {
    image:
      "https://images.unsplash.com/photo-1781504798134-31600e8579ba?auto=format&fit=crop&w=1400&q=80",
    quote:
      "In the course of history, there comes a time when humanity is called to shift to a new level of consciousness, to reach a higher moral ground.",
    author: "Wangari Maathai",
    role: "Nobel Peace Prize Laureate · Kenya",
  },
  {
    image:
      "https://images.unsplash.com/photo-1758270704840-0ac001215b55?auto=format&fit=crop&w=1400&q=80",
    quote:
      "The function of education is to teach one to think intensively and to think critically.",
    author: "Martin Luther King Jr.",
    role: "Civil Rights Leader",
  },
];

const INTERVAL_MS = 15_000;
const FADE_MS = 380;

export const AuthCarousel = {
  _timer: null as ReturnType<typeof setInterval> | null,
  _current: 0,
  _bgEls: [] as HTMLElement[],
  _quoteEl: null as HTMLElement | null,
  _dotEls: [] as HTMLButtonElement[],

  mounted(this: typeof AuthCarousel & { el: HTMLElement }) {
    SLIDES.forEach((s) => {
      const i = new Image();
      i.src = s.image;
    });
    this._build();
    this._show(0, false);
    this._startTimer();
  },

  destroyed(this: typeof AuthCarousel) {
    this._stopTimer();
  },

  _startTimer(this: typeof AuthCarousel) {
    this._timer = setInterval(() => {
      this._current = (this._current + 1) % SLIDES.length;
      this._show(this._current, true);
    }, INTERVAL_MS);
  },

  _stopTimer(this: typeof AuthCarousel) {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  },

  _build(this: typeof AuthCarousel & { el: HTMLElement }) {
    const el = this.el;

    this._bgEls = SLIDES.map((slide) => {
      const bg = document.createElement("div");
      Object.assign(bg.style, {
        position: "absolute",
        inset: "0",
        backgroundImage: `url('${slide.image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: "0",
        transition: "opacity 1.2s ease",
        willChange: "opacity, transform",
      });
      el.appendChild(bg);
      return bg;
    });

    // multi-stop scrim: transparent top → deep bottom
    const scrim = document.createElement("div");
    Object.assign(scrim.style, {
      position: "absolute",
      inset: "0",
      background:
        "linear-gradient(to bottom," +
        "  rgba(0,0,0,0.22) 0%," +
        "  rgba(0,0,0,0.04) 28%," +
        "  rgba(0,0,0,0.44) 62%," +
        "  rgba(0,0,0,0.84) 100%)",
      pointerEvents: "none",
    });
    el.appendChild(scrim);

    // logo
    const logo = document.createElement("div");
    Object.assign(logo.style, {
      position: "absolute",
      top: "2rem",
      left: "2.25rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      zIndex: "10",
    });
    logo.innerHTML = `
      <img src="/images/civil-aid-logo.png" alt="Civil Aid" style="height:32px;width:auto;filter:drop-shadow(0 1px 4px rgba(0,0,0,0.4));" />
    `;
    el.appendChild(logo);

    // bottom pane: quote + dots
    const bottom = document.createElement("div");
    Object.assign(bottom.style, {
      position: "absolute",
      bottom: "0",
      left: "0",
      right: "0",
      padding: "0 2.25rem 2.25rem",
      zIndex: "10",
    });

    const quoteEl = document.createElement("div");
    Object.assign(quoteEl.style, {
      marginBottom: "1.5rem",
      transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
    });
    bottom.appendChild(quoteEl);
    this._quoteEl = quoteEl;

    const dotsRow = document.createElement("div");
    Object.assign(dotsRow.style, {
      display: "flex",
      gap: "0.35rem",
      alignItems: "center",
    });

    this._dotEls = SLIDES.map((_, i) => {
      const dot = document.createElement("button");
      dot.setAttribute("aria-label", `Slide ${i + 1}`);
      Object.assign(dot.style, {
        height: "3px",
        borderRadius: "9999px",
        border: "none",
        cursor: "pointer",
        padding: "0",
        outline: "none",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      });
      dot.addEventListener("click", () => {
        this._current = i;
        this._show(i, true);
        this._stopTimer();
        this._startTimer();
      });
      dotsRow.appendChild(dot);
      return dot;
    });

    bottom.appendChild(dotsRow);
    el.appendChild(bottom);
  },

  _show(
    this: typeof AuthCarousel & { el: HTMLElement },
    index: number,
    animate: boolean,
  ) {
    const slide = SLIDES[index];

    this._bgEls.forEach((bg, i) => {
      if (i === index) {
        bg.style.opacity = "1";
        bg.style.animation = "none";
        void bg.offsetHeight;
        bg.style.animation = "carousel-kb 15s ease-out forwards";
      } else {
        bg.style.opacity = "0";
      }
    });

    if (this._quoteEl) {
      if (animate) {
        this._quoteEl.style.opacity = "0";
        this._quoteEl.style.transform = "translateY(12px)";
        setTimeout(() => {
          this._renderQuote(slide);
          if (this._quoteEl) {
            this._quoteEl.style.opacity = "1";
            this._quoteEl.style.transform = "translateY(0)";
          }
        }, FADE_MS + 60);
      } else {
        this._quoteEl.style.opacity = "1";
        this._quoteEl.style.transform = "translateY(0)";
        this._renderQuote(slide);
      }
    }

    this._dotEls.forEach((dot, i) => {
      const active = i === index;
      dot.style.width = active ? "2rem" : "0.375rem";
      dot.style.background = active
        ? "rgba(255,255,255,0.92)"
        : "rgba(255,255,255,0.32)";
    });
  },

  _renderQuote(this: typeof AuthCarousel, slide: Slide) {
    if (!this._quoteEl) return;
    this._quoteEl.innerHTML = `
      <p style="
        color: white;
        font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
        font-size: clamp(0.9375rem, 1.5vw, 1.125rem);
        font-style: italic;
        font-weight: 400;
        line-height: 1.72;
        margin: 0 0 0.875rem;
        text-shadow: 0 2px 8px rgba(0,0,0,0.5);
      ">&ldquo;${slide.quote}&rdquo;</p>
      <p style="
        color: rgba(255,255,255,0.58);
        font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        text-shadow: 0 1px 4px rgba(0,0,0,0.4);
        margin: 0;
      ">${slide.author}&ensp;&middot;&ensp;${slide.role}</p>
    `;
  },
};
